import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faUsers,
  faUserGraduate,
  faFileAlt,
  faPassport,
  faWallet,
  faDatabase,
  faUserCog,
  faUserTie,
  faChevronDown,
  faChevronUp,
  faUserShield,
  faIdCard,
  faUserFriends,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from '../context/AuthContext'

export default function Sidebar() {
    const location = useLocation();
    const { canAccessModule, user } = useAuth();
    const isStudent = user?.role === 'client';
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isLeadMenuOpen, setIsLeadMenuOpen] = useState(false);
    const [isVisaMenuOpen, setIsVisaMenuOpen] = useState(
      location.pathname.startsWith('/visa')
    );
    const [isMasterDataMenuOpen, setIsMasterDataMenuOpen] = useState(false);
    const [isFinanceMenuOpen, setIsFinanceMenuOpen] = useState(
      location.pathname.startsWith('/finance') || location.pathname.startsWith('/expenses') || location.pathname.startsWith('/create-invoice') || location.pathname.startsWith('/edit-invoice')
    );

    const show = (moduleKey) => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      if (user.role === 'consultant') return moduleKey !== 'staff_users';
      if (user.role === 'counselor') return canAccessModule(moduleKey);
      return canAccessModule(moduleKey);
    };
    const isCounselor = user?.role === 'counselor';

    useEffect(() => {
        const handleToggle = () => setIsCollapsed(prev => !prev);
        const handleMobileToggle = () => setIsMobileOpen(prev => !prev);
        const handleMobileClose = () => setIsMobileOpen(false);
        window.addEventListener('toggleSidebar', handleToggle);
        window.addEventListener('toggleMobileSidebar', handleMobileToggle);
        window.addEventListener('closeMobileSidebar', handleMobileClose);
        return () => {
            window.removeEventListener('toggleSidebar', handleToggle);
            window.removeEventListener('toggleMobileSidebar', handleMobileToggle);
            window.removeEventListener('closeMobileSidebar', handleMobileClose);
        };
    }, []);

    useEffect(() => {
        setIsMobileOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth > 992) setIsMobileOpen(false);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (isMobileOpen) {
            setIsCollapsed(false);
            document.body.classList.add('sidebar-mobile-open');
        } else {
            document.body.classList.remove('sidebar-mobile-open');
        }
        return () => document.body.classList.remove('sidebar-mobile-open');
    }, [isMobileOpen]);

    useEffect(() => {
        if (
          location.pathname.startsWith('/finance') ||
          location.pathname.startsWith('/expenses') ||
          location.pathname.startsWith('/create-invoice') ||
          location.pathname.startsWith('/edit-invoice') ||
          location.pathname.startsWith('/invoice-pdf') ||
          location.pathname.startsWith('/payment-receipt')
        ) {
            setIsFinanceMenuOpen(true);
        }
        if (location.pathname.startsWith('/visa')) {
            setIsVisaMenuOpen(true);
        }
    }, [location.pathname]);

    const toggleLeadMenu = () => setIsLeadMenuOpen(prev => !prev);
    const toggleVisaMenu = () => setIsVisaMenuOpen(prev => !prev);
    const toggleMasterDataMenu = () => setIsMasterDataMenuOpen(prev => !prev);
    const toggleFinanceMenu = () => setIsFinanceMenuOpen(prev => !prev);

    const showLeads = show('leads');
    const showStudents = show('students');
    const showCounselors = show('counselors');
    const showApplications = show('applications');
    const showVisas = show('visas');
    const showFinance = show('finance');
    const showVisitorLogs = show('visitor_logs');
    const showEmployees = show('employees');
    const showMasterData = show('master_data');
    const showStaffUsers = show('staff_users');
    const showManagement = showLeads || showStudents || showCounselors || showApplications || showVisas || showFinance || showVisitorLogs;
    const showHr = showEmployees || showStaffUsers;

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    return (
        <>
            {isMobileOpen && (
                <div
                    className="sidebar-backdrop"
                    onClick={() => setIsMobileOpen(false)}
                    aria-hidden="true"
                />
            )}
            <div
                className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'active' : ''}`}
                id="sidebar"
            >
                <div className="sidebar-header">
                    <img src="/images/logo.webp" alt="Logo" className="sidebar-logo" />
                    <h5 className="sidebar-title">
                      {isStudent ? 'Student Portal' : isCounselor ? 'Counselor Panel' : 'Admin Panel'}
                    </h5>
                </div>

                <ul className="sidebar-menu">
                    {isStudent ? (
                        <>
                            <li className={`menu-item ${location.pathname === '/dashboard' ? 'active' : ''}`}>
                                <Link to="/dashboard">
                                    <FontAwesomeIcon icon={faHome} />
                                    <span>Dashboard</span>
                                </Link>
                            </li>
                            <li className={`menu-item ${isActive('/my-registration') || isActive('/edit-student') ? 'active' : ''}`}>
                                <Link to="/my-registration">
                                    <FontAwesomeIcon icon={faIdCard} />
                                    <span>Student Registration</span>
                                </Link>
                            </li>
                            <li className={`menu-item ${isActive('/my-counselors') ? 'active' : ''}`}>
                                <Link to="/my-counselors">
                                    <FontAwesomeIcon icon={faUserFriends} />
                                    <span>Assigned Counselors</span>
                                </Link>
                            </li>
                            <li className={`menu-item ${isActive('/my-applications') || isActive('/edit-application') ? 'active' : ''}`}>
                                <Link to="/my-applications">
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>Application Form</span>
                                </Link>
                            </li>
                            <li className={`menu-item ${isActive('/my-visas') || isActive('/edit-visa') ? 'active' : ''}`}>
                                <Link to="/my-visas">
                                    <FontAwesomeIcon icon={faPassport} />
                                    <span>Visa Management</span>
                                </Link>
                            </li>
                        </>
                    ) : (
                        <>
                    <li className="menu-item">
                        <Link to="/dashboard">
                            <FontAwesomeIcon icon={faHome} />
                            <span>Dashboard</span>
                        </Link>
                    </li>

                    {showManagement && (
                    <li className="menu-section">
                        <span>MANAGEMENT</span>
                    </li>
                    )}

                    {showLeads && (
                    <li className="menu-item">
                        <div className="menu-item-parent" onClick={toggleLeadMenu}>
                            <div className="menu-item-content">
                                <FontAwesomeIcon icon={faUsers} />
                                <span>Lead Management</span>
                            </div>
                            <FontAwesomeIcon
                                icon={isLeadMenuOpen ? faChevronUp : faChevronDown}
                                className="submenu-arrow"
                            />
                        </div>
                        <ul className={`submenu ${isLeadMenuOpen ? 'open' : ''}`}>
                            <li>
                                <Link to="/leads"><span>Lead Details</span></Link>
                            </li>
                            <li>
                                <Link to="/follow-ups"><span>Follow Ups</span></Link>
                            </li>
                        </ul>
                    </li>
                    )}

                    {showStudents && (
                    <li className="menu-item">
                        <Link to="/students">
                            <FontAwesomeIcon icon={faUserGraduate} />
                            <span>Manage Students</span>
                        </Link>
                    </li>
                    )}
                    {showCounselors && (
                    <li className="menu-item">
                        <Link to="/counselors">
                            <FontAwesomeIcon icon={faUserTie} />
                            <span>Manage Counselors</span>
                        </Link>
                    </li>
                    )}
                    {showApplications && (
                    <li className="menu-item">
                        <Link to="/applications">
                            <FontAwesomeIcon icon={faFileAlt} />
                            <span>Manage Applications</span>
                        </Link>
                    </li>
                    )}
                    {showVisas && (
                    <li className="menu-item">
                        <div className="menu-item-parent" onClick={toggleVisaMenu}>
                            <div className="menu-item-content">
                                <FontAwesomeIcon icon={faPassport} />
                                <span>Visa Management</span>
                            </div>
                            <FontAwesomeIcon
                                icon={isVisaMenuOpen ? faChevronUp : faChevronDown}
                                className="submenu-arrow"
                            />
                        </div>
                        <ul className={`submenu ${isVisaMenuOpen ? 'open' : ''}`}>
                            {!isCounselor && (
                            <li>
                                <Link to="/visa-enquiries"><span>Visa Enquiries</span></Link>
                            </li>
                            )}
                            <li>
                                <Link to="/visa"><span>Manage Visas</span></Link>
                            </li>
                        </ul>
                    </li>
                    )}
                    {showFinance && (
                    <li className="menu-item">
                        <div className="menu-item-parent" onClick={toggleFinanceMenu}>
                            <div className="menu-item-content">
                                <FontAwesomeIcon icon={faWallet} />
                                <span>Finance</span>
                            </div>
                            <FontAwesomeIcon
                                icon={isFinanceMenuOpen ? faChevronUp : faChevronDown}
                                className="submenu-arrow"
                            />
                        </div>
                        <ul className={`submenu ${isFinanceMenuOpen ? 'open' : ''}`}>
                            {!isCounselor && (
                            <li>
                                <Link to="/finance"><span>Finance Overview</span></Link>
                            </li>
                            )}
                            <li>
                                <Link to="/finance/invoices"><span>Manage Invoices</span></Link>
                            </li>
                            {!isCounselor && (
                            <>
                            <li>
                                <Link to="/finance/bank-accounts"><span>Manage Bank Account</span></Link>
                            </li>
                            <li>
                                <Link to="/finance/expenses"><span>Manage Expenses</span></Link>
                            </li>
                            </>
                            )}
                        </ul>
                    </li>
                    )}
                    {showVisitorLogs && (
                    <li className="menu-item">
                        <Link to="/visitor-logs">
                            <FontAwesomeIcon icon={faUserCog} />
                            <span>Visitor Logs</span>
                        </Link>
                    </li>
                    )}

                    {showHr && (
                    <li className="menu-section">
                        <span>HR & OPERATIONS</span>
                    </li>
                    )}

                    {showEmployees && (
                    <li className="menu-item">
                        <Link to="/employees">
                            <FontAwesomeIcon icon={faUsers} />
                            <span>HR &mdash; Employees</span>
                        </Link>
                    </li>
                    )}

                    {showStaffUsers && (
                    <li className="menu-item">
                        <Link to="/staff-users">
                            <FontAwesomeIcon icon={faUserShield} />
                            <span>Staff Users</span>
                        </Link>
                    </li>
                    )}

                    {showMasterData && (
                    <>
                    <li className="menu-section">
                        <span>MASTER DATA</span>
                    </li>

                    <li className="menu-item">
                        <div className="menu-item-parent" onClick={toggleMasterDataMenu}>
                            <div className="menu-item-content">
                                <FontAwesomeIcon icon={faDatabase} />
                                <span>Master Data</span>
                            </div>
                            <FontAwesomeIcon
                                icon={isMasterDataMenuOpen ? faChevronUp : faChevronDown}
                                className="submenu-arrow"
                            />
                        </div>
                        <ul className={`submenu ${isMasterDataMenuOpen ? 'open' : ''}`}>
                            <li>
                                <Link to="/countries"><span>Manage Countries</span></Link>
                            </li>
                            <li>
                                <Link to="/universities"><span>Manage Universities</span></Link>
                            </li>
                            <li>
                                <Link to="/intakes"><span>Manage Intakes</span></Link>
                            </li>
                        </ul>
                    </li>
                    </>
                    )}
                        </>
                    )}

                </ul>
            </div>

            <style jsx>{`
                .menu-item-parent {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px 28px;
                    cursor: pointer;
                    position: relative;
                    transition: background-color 0.2s ease;
                }

                .menu-item-parent:hover {
                    background-color: rgba(255, 255, 255, 0.1);
                }

                .menu-item-content {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .submenu-arrow {
                    position: absolute;
                    right: 34px;
                    top: 50%;
                    transform: translateY(-50%);
                    font-size: 14px;
                }

                .submenu {
                    max-height: 0;
                    overflow: hidden;
                    transition: max-height 0.3s ease;
                    background-color: rgba(0, 0, 0, 0.2);
                }

                .submenu.open {
                    max-height: 280px;
                }

                .submenu li {
                    list-style: none;
                }
            `}</style>
        </>
    )
}
